import { differenceInSeconds } from 'date-fns'
import { createContext, ReactNode, useCallback, useContext, useRef } from 'react'
import { logFactory } from '../../util'
import { useClickTracking } from '../useClickTracking'
import { useSessionDuration } from '../useSessionDuration'

interface FormAnalyticsSummary<T> {
  didError: boolean
  errorCounts: Partial<Record<keyof T, number>>
  totalClicks: number
  totalSessionDurationInSeconds: number
  submitCount: number
  validInputTimings: Partial<Record<keyof T, number>>
}

interface FormAnalyticsTrackingContext<T> {
  onErrorsChange: (errors: Partial<Record<keyof T, any>>) => void
  onInputEntered: (key: keyof T) => void
  onInputValid: (key: keyof T) => void
  readSummary: () => FormAnalyticsSummary<T>
  reset: () => void
}

interface Props {
  children: ReactNode
}

const log = logFactory('useFormAnalytics')
const context = createContext({} as FormAnalyticsTrackingContext<unknown>)
const { Provider } = context

const FormAnalyticsProvider = <T extends Record<string, any>>({ children }: Props) => {
  const inputInteractions = useRef<Partial<Record<keyof T, Date>>>({} as Partial<Record<keyof T, Date>>)
  const inputValidTimings = useRef<Partial<Record<keyof T, number>>>({} as Partial<Record<keyof T, number>>)
  const submitCount = useRef<number>(0)
  const errorCounts = useRef<Partial<Record<keyof T, number>>>({})
  const { readDurationSeconds, reset: resetDuration } = useSessionDuration()
  const { readClickCount, reset: resetClickCount } = useClickTracking()

  const reset = useCallback(() => {
    errorCounts.current = {}
    submitCount.current = 0
    resetClickCount()
    resetDuration()
  }, [errorCounts, resetClickCount, resetDuration, submitCount])

  const onErrorsChange = useCallback(
    (errors: Partial<Record<keyof T, number>>) => {
      const newErrorCounts = Object.keys(errors).reduce<Partial<Record<keyof T, number>>>(
        (acc, key) => ({
          ...acc,
          [key]: (acc[key as keyof T] ?? 0) + 1,
        }),
        { ...errorCounts.current }
      )

      log('🧮 New error counts calculated', newErrorCounts)

      errorCounts.current = newErrorCounts
      submitCount.current = submitCount.current + 1
    },
    [errorCounts, submitCount]
  )

  const onInputEntered = useCallback(
    (key: keyof T) => {
      if (inputInteractions.current[key]) return

      log('🚪 Input entered for the first time', key)

      inputInteractions.current = {
        ...inputInteractions.current,
        [key]: new Date(),
      }
    },
    [inputInteractions]
  )

  const onInputValid = useCallback(
    (key: keyof T) => {
      if (inputValidTimings.current[key]) return
      if (!inputInteractions.current[key]) return

      log('👌 Input valid for the first time', key)

      const entered = inputInteractions.current[key]

      inputValidTimings.current = {
        ...inputValidTimings.current,
        [key]: differenceInSeconds(new Date(), entered as Date),
      }
    },
    [inputInteractions, inputValidTimings]
  )

  const readSummary = useCallback((): FormAnalyticsSummary<T> => {
    return {
      didError: Object.keys(errorCounts.current).length > 0,
      errorCounts: errorCounts.current,
      totalClicks: readClickCount(),
      totalSessionDurationInSeconds: readDurationSeconds(),
      submitCount: submitCount.current,
      validInputTimings: inputValidTimings.current,
    }
  }, [errorCounts, inputValidTimings, readClickCount, readDurationSeconds, submitCount])

  const contextValue = {
    onErrorsChange,
    onInputEntered,
    onInputValid,
    readSummary,
    reset,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useFormAnalytics = <T extends Record<string, any>>(): FormAnalyticsTrackingContext<T> =>
  // @ts-expect-error unknown vs generic type
  useContext<FormAnalyticsTrackingContext<T>>(context)

export { FormAnalyticsProvider, useFormAnalytics }
